import { Box, BoxProps } from '@chakra-ui/react'
import { ReactNode, forwardRef, useRef } from 'react'

import {
  BOUNDARY_ATTRIBUTE,
  useClickOutsideBoundary,
} from '../../hooks/useClickOutsideBoundary'

export type ClickOutsideBoundaryProps = BoxProps & {
  /**
   * Unique identifier for this boundary.
   * Used to match portaled elements with the same boundary ID.
   */
  boundaryId: string
  /**
   * Handler called when a click is detected outside the boundary
   */
  onClickOutside?: () => void
  /**
   * Whether click-outside detection is enabled.
   * @default true
   */
  enabled?: boolean
  /**
   * Children to render inside the boundary
   */
  children: ReactNode
  /**
   * Whether to automatically detect and exclude floating UI elements
   * @default true
   */
  excludeFloatingUI?: boolean
}

/**
 * A wrapper component that detects clicks outside its boundary.
 *
 * This component is designed to work well with portaled elements like dropdown menus.
 * It uses a data-attribute system to mark elements that should be considered "inside"
 * the boundary, even if they're rendered elsewhere in the DOM via portals.
 *
 * ## Basic Usage
 *
 * ```tsx
 * <ClickOutsideBoundary
 *   boundaryId="my-popover"
 *   onClickOutside={() => setIsOpen(false)}
 *   enabled={isOpen}
 * >
 *   <PopoverContent>
 *     <SelectInput /> // Dropdown menus work correctly!
 *   </PopoverContent>
 * </ClickOutsideBoundary>
 * ```
 *
 * ## With Portaled Elements
 *
 * If you have custom portaled content that should be considered "inside" the boundary,
 * mark it with the `data-click-boundary` attribute:
 *
 * ```tsx
 * <Portal>
 *   <div data-click-boundary="my-popover">
 *     Custom portaled content
 *   </div>
 * </Portal>
 * ```
 */
export const ClickOutsideBoundary = forwardRef<
  HTMLDivElement,
  ClickOutsideBoundaryProps
>(
  (
    {
      boundaryId,
      onClickOutside,
      enabled = true,
      children,
      excludeFloatingUI = true,
      ...boxProps
    },
    forwardedRef,
  ) => {
    const internalRef = useRef<HTMLDivElement>(null)
    const ref = (forwardedRef as React.RefObject<HTMLDivElement>) ?? internalRef

    useClickOutsideBoundary({
      ref,
      boundaryId,
      onClickOutside: onClickOutside ?? (() => {}),
      enabled: enabled && Boolean(onClickOutside),
      excludeFloatingUI,
    })

    return (
      <Box ref={ref} {...{ [BOUNDARY_ATTRIBUTE]: boundaryId }} {...boxProps}>
        {children}
      </Box>
    )
  },
)

ClickOutsideBoundary.displayName = 'ClickOutsideBoundary'

/**
 * A marker component for portaled elements that should be considered
 * "inside" a click-outside boundary.
 *
 * Use this to wrap portaled content that shouldn't trigger the
 * click-outside handler.
 *
 * ```tsx
 * <Portal>
 *   <BoundaryMarker boundaryId="my-popover">
 *     <CustomDropdownMenu />
 *   </BoundaryMarker>
 * </Portal>
 * ```
 */
export const BoundaryMarker = ({
  boundaryId,
  children,
  ...boxProps
}: BoxProps & {
  boundaryId: string | string[]
  children: ReactNode
}) => {
  const ids = Array.isArray(boundaryId) ? boundaryId : [boundaryId]
  return (
    <Box {...{ [BOUNDARY_ATTRIBUTE]: ids.join(',') }} {...boxProps}>
      {children}
    </Box>
  )
}
